import React, { useState } from "react";
import "./SharePointAndInsightPDMImplementationPage.css";
import BannerImage from "../../../assets/other_image/sharePoint.jpg";
import { NavLink } from "react-router-dom";
import Letsgetintouch from "../../PLMImplementation/BusinessProcessAndChangeManagement/Letsgetintouch";
import Banner from "../SolutionBanner/Banner";
import FloatingContactUs from "../../floting_contact/FloatingContactUs";
import BookDemoSection from "../PolarianALMSolution/BookDemoSection";
import SharePointAndInsightPDMImplementationOverview from "./SharePointAndInsightPDMImplementationOverview";
import { Helmet } from "react-helmet-async";

/* === FAQ Data === */
const faqData = [
  {
    question: "What is Microsoft SharePoint?",
    answer:
      "SharePoint is Microsoft’s leading collaboration and content management platform, enabling organizations to share information, manage documents, and streamline processes."
  },
  {
    question: "How does Insight PDM complement SharePoint?",
    answer:
      "Insight PDM integrates CAD data and engineering workflows into SharePoint, allowing seamless collaboration between design and business teams."
  },
  {
    question: "Can SharePoint handle enterprise-grade projects?",
    answer:
      "Yes. SharePoint supports enterprise-level deployment, scalability, and integration with ERP, CRM, and legacy systems."
  },
  {
    question: "What business processes can SharePoint automate?",
    answer:
      "From document approval workflows to NPD, APQP, PPAP, and supplier management, SharePoint enables end-to-end process automation."
  },
  {
    question: "Can SharePoint be customized?",
    answer:
      "Absolutely. Nextech specializes in SharePoint customization, including web parts, UI design, workflows, and system integrations."
  },
  {
    question: "How long does it take to implement SharePoint or Insight PDM?",
    answer:
      "Implementation timelines depend on complexity — from a few weeks for simple setups to several months for enterprise-grade solutions."
  },
  {
    question: "Do you provide training and support?",
    answer:
      "Yes. Nextech offers role-based training and 24/7 managed support to ensure long-term success."
  }
];

/* === Reusable Components === */
const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item">
      <button
        className="faq-question"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span>{question}</span>
        <span>{open ? "−" : "+"}</span>
      </button>
      {open && <div className="faq-answer">{answer}</div>}
    </div>
  );
};

const SectionHeader = ({ title, subtitle }) => (
  <header className="section-header">
    <h2 className="section-title">{title}</h2>
    {subtitle && <p className="section-subtitle">{subtitle}</p>}
  </header>
);

const TileGrid = ({ items }) => (
  <div className="tile-grid-3x2">
    {items.map((item, index) => (
      <article className="feature-tile" key={index}>
        {item.icon && (
          <div className="tile-icon">
            {typeof item.icon === "string" && item.icon.startsWith("http") ? (
              <img src={item.icon} alt={item.title} width="48" height="48" />
            ) : (
              <span className="emoji-icon">{item.icon}</span>
            )}
          </div>
        )}
        <h3 className="tile-title">{item.title}</h3>
        <p className="tile-content">{item.content}</p>
      </article>
    ))}
  </div>
);

/* === Page Component === */
const SharePointAndInsightPDMImplementationPage = () => {
  /* --- Featured Solutions --- */
  const featuredSolutionsItems = [
    {
      title: "SharePoint Implementation & Customization",
      content:
        "Deploy, configure, and customize SharePoint to meet your unique business needs — from intranets and extranets to enterprise workflows."
    },
    {
      title: "Insight PDM Integration",
      content:
        "Enable robust CAD data and process management by integrating Insight PDM with your SharePoint environment for seamless collaboration between design and business teams."
    },
    {
      title: "Document & Process Management",
      content:
        "Simplify document lifecycle management with version control, metadata, approval workflows, and secure access."
    },
    {
      title: "Business Intelligence & Reporting",
      content:
        "Leverage SharePoint dashboards and analytics for real-time insights into resources, projects, and enterprise performance."
    },
    {
      title: "Collaboration & Resource Management",
      content:
        "Empower teams with tools for meetings, surveys, project management, and knowledge sharing."
    },
    {
      title: "Enterprise System Integration",
      content:
        "Integrate SharePoint with ERP, CRM, and legacy systems for a connected enterprise ecosystem."
    }
  ];

  /* --- Our Solutions --- */
  const ourSolutionsItems = [
    {
      icon: "🛠️",
      title: "Complete SharePoint Implementation",
      content:
        "End-to-end deployment of SharePoint 2013/2010/2007, SharePoint Foundation, and WSS versions."
    },
    {
      icon: "📐",
      title: "Insight PDM Enablement",
      content:
        "Seamless setup for managing CAD data, revisions, and engineering processes."
    },
    {
      icon: "🎨",
      title: "Custom UI & Workflow Design",
      content:
        "Tailored user-friendly designs and process workflows that align with your brand and business goals."
    },
    {
      icon: "🔗",
      title: "Enterprise System Integration",
      content:
        "Connect SharePoint with ERP, CRM, and legacy platforms for efficient data flow and decision-making."
    },
    {
      icon: "📘",
      title: "Role-Based Training",
      content:
        "Equip business and technical users with practical skills to manage and extend SharePoint independently."
    },
    {
      icon: "🛡️",
      title: "Managed Support",
      content:
        "Proactive maintenance, upgrades, scalability enhancements, and 24/7 support."
    }
  ];

  /* --- Why Choose Nextech --- */
  const whyNextechItems = [
    {
      icon: "🧠",
      title: "Deep Expertise in SharePoint Technology",
      content:
        "Proven success in deployment, customization, and optimization of SharePoint across industries."
    },
    {
      icon: "📊",
      title: "Insight PDM Integration Leadership",
      content:
        "Strong experience in CAD and engineering data management solutions."
    },
    {
      icon: "🤝",
      title: "Business–IT Alignment",
      content:
        "We bridge the gap between technical complexity and business outcomes."
    },
    {
      icon: "📈",
      title: "Scalable Solutions",
      content:
        "From small teams to enterprise-level deployments, our solutions evolve with your business."
    },
    {
      icon: "✨",
      title: "Customization & Innovation",
      content:
        "Expertise in web part development, UI customization, workflow automation, and intelligent document numbering."
    },
    {
      icon: "🧰",
      title: "Comprehensive Enablement & Support",
      content:
        "Beyond implementation — we provide training, optimization, and long-term support for sustainable success."
    }
  ];

  return (
    <>
      {/* ✅ Meta Tags for SEO */}
    <Helmet>
      <title>SharePoint Insight PDM Implementation Services in Pune | Nextech PLM</title>
      <meta name="description" content="Implement SharePoint Insight PDM effectively with Nextech PLM in Pune, India. Improve collaboration and data management across teams." />
      <meta name="keywords" content="SharePoint PLM implementation Pune, Insight PDM services India, document management PLM, SharePoint integration Teamcenter, engineering document control" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <link rel="canonical" href="https://nextechplm.com/sharepoint-insight-pdm-implementation" />
      <meta property="og:site_name" content="NexTech PLM Solutions" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="SharePoint Insight PDM Implementation Services in Pune | Nextech PLM" />
      <meta property="og:description" content="Implement SharePoint Insight PDM effectively with Nextech PLM in Pune, India. Improve collaboration and data management across teams." />
      <meta property="og:url" content="https://nextechplm.com/sharepoint-insight-pdm-implementation" />
      <meta name="twitter:title" content="SharePoint Insight PDM Implementation Services in Pune | Nextech PLM" />
      <meta name="twitter:description" content="Implement SharePoint Insight PDM effectively with Nextech PLM in Pune, India. Improve collaboration and data management across teams." />
</Helmet>
    
    <main className="sharepoint-solution-page">
      <Banner image={BannerImage} heading="SharePoint & Insight PDM Implementation" />
      <FloatingContactUs />
      <SharePointAndInsightPDMImplementationOverview />

     {/* === Business Benefits === */}
<section className="section benefits-section">
  <div className="container">
    <div className="row g-4 align-items-center">

      {/* Benefits Section */}
      <div className="col-md-6 col-12">
        <div className="benefits-card">
          <h3 className="benefits-heading">
            Business Benefits of SharePoint & Insight PDM with NexTech
          </h3>
          <ul className="benefits-list">
            <li>
              <span className="benefit-icon">✔</span> Enhance enterprise collaboration across departments and geographies.
            </li>
            <li>
              <span className="benefit-icon">✔</span> Accelerate decision-making with faster access to business-critical data.
            </li>
            <li>
              <span className="benefit-icon">✔</span> Streamline processes by automating NPD, APQP, PPAP, and supplier management workflows.
            </li>
            <li>
              <span className="benefit-icon">✔</span> Secure & scalable deployments with SharePoint’s enterprise-grade architecture.
            </li>
            <li>
              <span className="benefit-icon">✔</span> Optimize Product Data Management (PDM) with seamless CAD (Solid Edge & AutoCAD) data integration.
            </li>
            <li>
              <span className="benefit-icon">✔</span> Improve customer & supplier engagement via extranet portals.
            </li>
            <li>
              <span className="benefit-icon">✔</span> Future-proof your strategy with scalable SharePoint & PDM solutions.
            </li>
          </ul>
        </div>
      </div>

      {/* Quick Links Section */}
      <div className="col-md-6 col-12 d-flex align-items-center">
        <div className="quick-links">
          <h4>Quick Links</h4>
          <ul>
            <li>
              <NavLink to={"/sharepoint/document-management"}>
                Document Management
              </NavLink>
            </li>
            <li>
              <NavLink to={"/sharepoint/workflow-automation"}>
                Workflow Automation
              </NavLink>
            </li>
            <li>
              <NavLink to={"/sharepoint/cad-data-integration"}>
                CAD Data Integration
              </NavLink>
            </li>
            <li>
              <NavLink to={"/sharepoint/extranet-portals"}>
                Extranet Portals
              </NavLink>
            </li>
            <li>
              <NavLink to={"/sharepoint/scalable-deployment"}>
                Scalable Deployment
              </NavLink>
            </li>
            <li>
              <NavLink to={"/sharepoint/collaboration-tools"}>
                Collaboration Tools
              </NavLink>
            </li>
            <li>
              <NavLink to={"/sharepoint/insight-pdm-integration"}>
                Insight PDM Integration
              </NavLink>
            </li>
          </ul>
        </div>
      </div>

    </div>
  </div>
</section>

      <BookDemoSection />

      {/* === Featured Solutions === */}
      <section className="section featured-solutions-section">
        <div className="container">
          <SectionHeader
            title="Our SharePoint & Insight PDM Featured Solutions"
            subtitle="Explore the capabilities that empower collaboration, compliance, and digital transformation."
          />
          <TileGrid items={featuredSolutionsItems} />
          <p className="section-outro">
            With <strong>NexTech PLM’s expertise</strong>, these SharePoint &
            Insight PDM solutions are not just deployed — they’re optimized to
            maximize collaboration, control, and innovation across your
            enterprise.
          </p>
        </div>
      </section>

      {/* === Video Section === */}
      <section className="video-promo">
        <div className="video-promo-container">
          <div className="video-promo-content">
            <h2 className="video-promo-heading">
              Transform Product Data Management with SharePoint & Insight PDM
            </h2>
            <p className="video-promo-text">
              Discover how our SharePoint & Insight PDM solutions streamline
              collaboration, secure data management, and drive efficiency across
              your enterprise.
            </p>
            <ul className="video-promo-features">
              <li className="feature-item">
                <span className="feature-icon">📂</span> Centralize document &
                product data management
              </li>
              <li className="feature-item">
                <span className="feature-icon">⚡</span> Automate workflows like
                NPD, APQP & PPAP
              </li>
              <li className="feature-item">
                <span className="feature-icon">🌍</span> Enhance collaboration
                across global teams
              </li>
            </ul>
          </div>
          <div className="video-promo-player">
            <div className="video-wrapper">
              <iframe
                src="https://www.youtube.com/embed/htpg8CuD1Ec?autoplay=0&rel=0&modestbranding=1"
                title="SharePoint & Insight PDM Overview"
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              ></iframe>
            </div>
            <p className="video-caption">
              Watch our quick demo to see SharePoint & Insight PDM in action
            </p>
          </div>
        </div>
      </section>

      {/* === Our Solutions === */}
      <section className="section our-solutions-section">
        <div className="container">
          <SectionHeader title="Our SharePoint & Insight PDM Services" />
          <p className="section-intro">
            At <strong>NexTech PLM</strong>, we don’t just deploy SharePoint —
            we transform it into a strategic asset for your business through
            end-to-end enablement, integration, and optimization.
          </p>
          <TileGrid items={ourSolutionsItems} />
          <p className="section-outro">
            With <strong>NexTech PLM</strong>, your SharePoint environment
            evolves into a powerful collaboration and data management platform
            that empowers every level of your organization.
          </p>
        </div>
      </section>

      {/* === Why Choose Nextech === */}
      <section className="section why-nextech-section">
        <div className="container">
          <SectionHeader title="Why Choose NexTech for SharePoint & Insight PDM?" />
          <p className="section-intro">
            More than just implementation — we deliver enterprise-grade,
            scalable, and fully integrated SharePoint & PDM solutions built for
            business transformation.
          </p>
          <TileGrid items={whyNextechItems} />
          <p className="section-outro">
            With NexTech, you gain a strategic partner focused on delivering
            measurable value through innovation, expertise, and long-term
            success.
          </p>
        </div>
      </section>

      {/* === FAQs === */}
      <section className="section faq-section">
        <div className="container">
          <SectionHeader title="SharePoint & Insight PDM FAQs" />
          <div className="faq-container">
            {faqData.map((item, i) => (
              <FAQItem key={i} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section>

      <Letsgetintouch />
    </main>
</>
  );
};

export default SharePointAndInsightPDMImplementationPage;
